import React, { Component } from "react";
import * as PropTypes from "prop-types";
import { Checkbox, Col, Form, Modal, Row, Switch, Tabs } from "antd";

import moment from "moment";

import { annualLeaveService } from "./../../redux/services";

import {
	UIDrawerForm,
	UIFormSection,
	UIPageListing,
	UISwitchField,
} from "./../../components";

import { isCombinarFaixaDiurnaNoturnaEnabled } from "./../../helpers/auth";

import {
	STEPS,
	ABSENCE_CONTROL_TYPES,
	DELAY_CONTROL_TYPES,
	EXTRA_CONTROL_TYPES,
	EXTRA_DAYS,
	AnnualLeaveInfoBanner,
} from "./create";

const getPeriodStatus = ({ vigencia_inicio, vigencia_final }) => {
	if (moment().isBefore(vigencia_inicio, "day")) {
		return { label: "Futuro", tone: "info" };
	}

	if (moment().isAfter(vigencia_final, "day")) {
		return { label: "Encerrado", tone: "neutral" };
	}

	return { label: "Vigente", tone: "success" };
};

class Show extends Component {
	static propTypes = {
		visible: PropTypes.bool.isRequired,
		onClose: PropTypes.func.isRequired,
		external: PropTypes.bool,
	};

	constructor(props) {
		super(props);

		this.state = {
			isLoading: true,
			uuid: 0,
			item: {},
			general_key: null,
		};
	}

	onOpen = (uuid, key) => {
		this.setState({
			isLoading: true,
			uuid: uuid,
			item: {},
			general_key: key,
		});

		annualLeaveService.show({ uuid })
			.then((response) => {
				this.setState({
					isLoading: false,
					item: response.data.data,
				});
			})
			.catch((data) => {
				Modal.error({
					title: "Ocorreu um erro!",
					content: String(data),
					onOk: () => {
						// Force close
						return this.onClose();
					}
				});
			});
	};

	resetFields = () => {
		this.setState({
			item: {},
		});
	};

	onClose = () => {
		// Reset fields
		this.resetFields();

		// Callback
		this.props.onClose();
	};

	periodsColumns = () => [
		{
			title: "Período",
			render: (period) => (
				<div className="cell-icon-info">
					<div className="cell-icon-info-icon">
						<i className="fal fa-calendar-alt" />
					</div>
					<div className="cell-icon-info-text">
						<span className="cell-icon-info-title">Período {period.index}</span>
					</div>
				</div>
			),
		},
		{
			title: "Data inicial",
			render: (period) => moment(period.vigencia_inicio).format("DD/MM/YYYY"),
		},
		{
			title: "Data final",
			render: (period) => moment(period.vigencia_final).format("DD/MM/YYYY"),
		},
		{
			title: "Situação",
			className: "active no-ellipsis",
			width: 150,
			render: (period) => {
				const { label, tone } = getPeriodStatus(period);

				return (
					<span className={`status-tag status-tag-${tone}`}>
						<i className="status-tag-dot" />
						{label}
					</span>
				);
			},
		},
	];

	render() {
		const { visible } = this.props;
		const { isLoading, item, general_key } = this.state;

		const periods = (item.banco_horas_vigencia ?? []).map((period, index) => ({ ...period, index: index + 1 }));
		const multiplyRanges = Object.keys(EXTRA_DAYS).filter((day) => item[`multiplicar_percentual_${day}`] == 1);

		return (
			<UIDrawerForm
				visible={visible}
				width={1100}
				onClose={this.onClose}
				isLoading={isLoading}
				showBtnSave={false}
				icon={<i className="fal fa-hourglass-half" />}
				title="Visualizar banco de horas"
				subtitle="Consulte as regras do banco de horas e a quem ele se aplica.">
				<Form layout="vertical">
					<Tabs defaultActiveKey={general_key != null ? general_key : "general"}>
						<Tabs.TabPane forceRender tab={<span className="tab-icon-label">{STEPS[0].icon} {STEPS[0].label}</span>} key="general">
							<UIFormSection
								icon={<i className="fal fa-clipboard-list" />}
								title="Informações gerais"
								description="Identificação e situação do banco de horas.">
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item label="Descrição">
											{item.descricao ?? "-"}
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item label="Status">
											<UISwitchField
												bordered
												title="Ativo"
												description={item.is_active == 1 ? "Banco de horas ativo e usado no cálculo do ponto." : "Banco de horas inativo e fora do cálculo do ponto."}>
												<Switch disabled checked={item.is_active == 1} />
											</UISwitchField>
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item label="Criação">
											{item.created_at ? moment(item.created_at).calendar() : "-"}
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item label="Última atualização">
											{item.updated_at ? moment(item.updated_at).calendar() : "-"}
										</Form.Item>
									</Col>
								</Row>
							</UIFormSection>
							<AnnualLeaveInfoBanner description="As horas extras, faltas e atrasos dos funcionários vinculados entram no banco conforme as regras deste cadastro." />
						</Tabs.TabPane>
						<Tabs.TabPane forceRender tab={<span className="tab-icon-label">{STEPS[1].icon} {STEPS[1].label}</span>} key="periods">
							<UIFormSection
								icon={<i className="fal fa-calendar-alt" />}
								title="Períodos"
								description="Datas de início e fim em que o banco de horas fica valendo.">
								<UIPageListing
									listType="list"
									showListTypeChange={false}
									enablePagination={false}
									enableSearch={false}
									data={periods}
									columns={this.periodsColumns()}
								/>
							</UIFormSection>
						</Tabs.TabPane>
						<Tabs.TabPane forceRender tab={<span className="tab-icon-label">{STEPS[2].icon} {STEPS[2].label}</span>} key="extras">
							<UIFormSection
								icon={<i className="fal fa-business-time" />}
								title="Controle de extras"
								description="Define quais horas extras entram no banco e o limite de cada período.">
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item label="Controle de extras">
											{EXTRA_CONTROL_TYPES[item.tipo_controle_extra] ?? "-"}
										</Form.Item>
									</Col>
									{["diario", "semanal", "mensal"].includes(item.tipo_controle_extra) && (
										<Col xs={24} md={12}>
											<Form.Item label="Limite máximo de extras">
												{item.limite_maximo_de_extras?.substr(0, 5) ?? "-"}
											</Form.Item>
										</Col>
									)}
								</Row>
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item label="Percentual">
											<UISwitchField
												bordered
												title="Multiplicar pelo percentual"
												description="Nas faixas selecionadas, as horas extras entram no banco acrescidas do percentual da faixa.">
												<Switch disabled checked={item.nao_multiplicar_pelo_percentual == 1} />
											</UISwitchField>
										</Form.Item>
										{item.nao_multiplicar_pelo_percentual == 1 && (
											<Form.Item label="Faixas de horário">
												{multiplyRanges.length ? multiplyRanges.map((day) => EXTRA_DAYS[day]).join(", ") : "-"}
											</Form.Item>
										)}
									</Col>
									{["primeira_faixa_entrada", "ultima_faixa_entrada", "diario", "semanal", "mensal"].includes(item.tipo_controle_extra) && isCombinarFaixaDiurnaNoturnaEnabled() && (
										<Col xs={24} md={12}>
											<Form.Item label="Faixas diurna e noturna">
												<UISwitchField
													bordered
													title="Não separar faixa diurna e noturna"
													description="As extras diurnas e noturnas são tratadas como uma faixa só.">
													<Switch disabled checked={item.combinar_faixa_diurna_noturna == 1} />
												</UISwitchField>
											</Form.Item>
										</Col>
									)}
								</Row>
							</UIFormSection>
							<UIFormSection
								icon={<i className="fal fa-calendar-times" />}
								title="Ignorar faixa de extras"
								description="As horas extras feitas nos dias marcados não entram no banco.">
								<Row gutter={16}>
									{Object.entries(EXTRA_DAYS).map(([day, label]) => (
										<Col key={day} xs={12} md={8}>
											<Form.Item>
												<Checkbox disabled checked={item[`extra_${day}`] == 1}>{label}</Checkbox>
											</Form.Item>
										</Col>
									))}
								</Row>
							</UIFormSection>
						</Tabs.TabPane>
						<Tabs.TabPane forceRender tab={<span className="tab-icon-label">{STEPS[3].icon} {STEPS[3].label}</span>} key="absences">
							<UIFormSection
								icon={<i className="fal fa-user-times" />}
								title="Faltas"
								description="Define quais faltas são descontadas do banco. O que passar do limite continua como falta no cartão de ponto.">
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item label="Controle de faltas">
											{ABSENCE_CONTROL_TYPES[item.controle_faltas] ?? "-"}
										</Form.Item>
									</Col>
									{!["nenhuma_hora_falta", "todas_horas_faltas"].includes(item.controle_faltas) && (
										<Col xs={24} md={12}>
											<Form.Item label="Limite máximo de faltas">
												{item.limite_maximo_de_faltas?.substr(0, 5) ?? "-"}
											</Form.Item>
										</Col>
									)}
								</Row>
							</UIFormSection>
							<UIFormSection
								icon={<i className="fal fa-user-clock" />}
								title="Atrasos"
								description="Define se os atrasos também são descontados do banco.">
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item label="Controle de atrasos">
											{DELAY_CONTROL_TYPES[item.controle_atraso] ?? "-"}
										</Form.Item>
									</Col>
								</Row>
							</UIFormSection>
						</Tabs.TabPane>
						<Tabs.TabPane forceRender tab={<span className="tab-icon-label">{STEPS[4].icon} {STEPS[4].label}</span>} key="ties">
							<UIFormSection
								icon={<i className="fal fa-building" />}
								title="Empresas e departamentos"
								description="Define a quais empresas e departamentos o banco de horas se aplica.">
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item label="Empresa">
											{item.companies?.length ? item.companies.map((company) => company.nome).join(", ") : "Todas"}
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item label="Departamento">
											{item.departments?.length ? item.departments.map((department) => department.nome).join(", ") : "Todos"}
										</Form.Item>
									</Col>
								</Row>
							</UIFormSection>
						</Tabs.TabPane>
					</Tabs>
				</Form>
			</UIDrawerForm>
		)
	}
}

export default Show;
